import i18n from 'i18n/config';
import { store } from 'redux/store';
import { translate } from 'hooks/useTranslate';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { HoldingClass } from 'types';

import { formatNumber } from 'utils/formats';

import { getHoldingClasses } from 'modules/partners/api/getHoldingClasses';
import { isShareClassVisible } from 'modules/trading/utils/isShareClassVisible';
import { getHoldingClassById } from 'modules/partners/api/getHoldingClassById';
import { CustomAsyncSelectOption } from 'modules/_shared/components/CustomAsyncSelect/types/CustomAsyncSelectOption';

type UseGetHoldingClassOptionsProps = {
  societyId?: string;
  holdingClassId?: string | null;
  partnerId?: string;
  reservedSharesByShareClass?: Record<string, number>;
};

const FIELDS = 'name sharesCount';

const labelByPlatform = (
  holdingClass: HoldingClass,
  partnerId?: string,
  reservedSharesByShareClass?: Record<string, number>
): string => {
  const { name, sharesCount, partnerCurrentSharesCount } = holdingClass;

  const gross = partnerId
    ? partnerCurrentSharesCount || 0
    : sharesCount?.actual || 0;
  const reserved =
    partnerId && reservedSharesByShareClass
      ? reservedSharesByShareClass[holdingClass._id as string] || 0
      : 0;
  const sharesTotal = partnerId ? Math.max(0, gross - reserved) : gross;

  const sharesSuffix = translate('shares');

  if (isShareClassVisible()) {
    return `${name} | ${formatNumber(sharesTotal)} ${sharesSuffix}`;
  }

  return `${formatNumber(sharesTotal)} ${sharesSuffix}`;
};

const mapOption = (
  holdingClass: HoldingClass,
  partnerId?: string,
  reservedSharesByShareClass?: Record<string, number>
): CustomAsyncSelectOption => {
  const label = labelByPlatform(
    holdingClass,
    partnerId,
    reservedSharesByShareClass
  );
  const gross = holdingClass.partnerCurrentSharesCount || 0;
  const reserved =
    partnerId && reservedSharesByShareClass
      ? reservedSharesByShareClass[holdingClass._id as string] || 0
      : 0;
  const net =
    partnerId && reservedSharesByShareClass
      ? Math.max(0, gross - reserved)
      : holdingClass.partnerCurrentSharesCount;

  return {
    name: holdingClass.name,
    value: holdingClass._id,
    label,
    sharesCount: net,
  };
};

const mapOptions = (
  holdingClasses: HoldingClass[],
  partnerId?: string,
  reservedSharesByShareClass?: Record<string, number>
): CustomAsyncSelectOption[] =>
  holdingClasses
    ?.filter(
      (holdingClass: HoldingClass) =>
        (holdingClass?.sharesCount?.actual || 0) > 0
    )
    .map((holdingClass: HoldingClass) =>
      mapOption(holdingClass, partnerId, reservedSharesByShareClass)
    );

export const useGetHoldingClassOptions = ({
  societyId,
  holdingClassId,
  partnerId,
  reservedSharesByShareClass,
}: UseGetHoldingClassOptionsProps) => {
  const [selectedHoldingClass, setSelectedHoldingClass] = useState<string>('');
  const [selectedHoldingClassOption, setSelectedHoldingClassOption] =
    useState<CustomAsyncSelectOption | null>(null);
  const [holdingClassOptions, setHoldingClassOptions] = useState<
    CustomAsyncSelectOption[]
  >([]);
  const [fetchedHoldingClasses, setFetchedHoldingClasses] = useState<
    HoldingClass[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  const handleChangeSelectedHoldingClass = (
    selectedOption: CustomAsyncSelectOption | null
  ) => {
    setSelectedHoldingClass(selectedOption?.value || '');
    setSelectedHoldingClassOption(selectedOption || null);
  };

  const handleSearchHoldingClassByTerm = useCallback(
    async (searchTerm: string) => {
      if (!searchTerm || !societyId) return;

      try {
        const holdingClasses = await store.dispatch(
          getHoldingClasses({
            societyId,
            partnerId,
            params: { fields: FIELDS, searchTerm },
          })
        );

        return mapOptions(
          holdingClasses,
          partnerId,
          reservedSharesByShareClass
        );
      } catch (error) {
        setIsError(true);
      }
    },
    [societyId, partnerId, reservedSharesByShareClass]
  );

  const fetchHoldingClasses = useCallback(async () => {
    if (!societyId) return;

    setIsLoading(true);
    setIsError(false);

    try {
      if (holdingClassId) {
        const holdingClass = await store.dispatch(
          getHoldingClassById({
            holdingClassId,
            partnerId,
          })
        );

        setFetchedHoldingClasses([holdingClass]);
        setIsLoading(false);
        return;
      }

      const holdingClasses = await store.dispatch(
        getHoldingClasses({
          societyId,
          partnerId,
          params: { fields: FIELDS },
        })
      );

      setFetchedHoldingClasses(holdingClasses);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      setIsError(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [holdingClassId, partnerId, societyId, i18n.language]);

  useEffect(() => {
    fetchHoldingClasses();
  }, [fetchHoldingClasses]);

  const displayOptions = useMemo(() => {
    if (!fetchedHoldingClasses?.length) return [];
    if (holdingClassId) {
      const hc = fetchedHoldingClasses[0];
      return [mapOption(hc, partnerId, reservedSharesByShareClass)];
    }
    return mapOptions(
      fetchedHoldingClasses,
      partnerId,
      reservedSharesByShareClass
    );
  }, [
    fetchedHoldingClasses,
    partnerId,
    reservedSharesByShareClass,
    holdingClassId,
    i18n.language,
  ]);

  useEffect(() => {
    if (holdingClassId) {
      const opt = displayOptions[0] || null;
      setSelectedHoldingClassOption(opt);
      setSelectedHoldingClass(opt?.value ? String(opt.value) : '');
      return;
    }

    setHoldingClassOptions(displayOptions);
    setSelectedHoldingClassOption((prev) => {
      const id = prev?.value as string | undefined;
      return (
        displayOptions.find((o) => o.value === id) || displayOptions[0] || null
      );
    });
    setSelectedHoldingClass((prev) => {
      const next =
        displayOptions.find((o) => o.value === prev) ||
        displayOptions[0] ||
        null;
      return next?.value ? String(next.value) : '';
    });
  }, [displayOptions, holdingClassId]);

  return {
    data: {
      selectedHoldingClass,
      selectedHoldingClassOption,
      holdingClassOptions,
    },
    actions: {
      handleChangeSelectedHoldingClass,
      handleSearchHoldingClassByTerm,
    },
    isLoading,
    isError,
  };
};
